import classNames from 'classnames'
import React, { useContext, useEffect, useState } from 'react'
import { ButtonType, Columns, CustomButton, FormSavePrompt, InfoTooltip, InputWrapper } from '../../components'
import { toMenuOptions, WemoloDropdown, WemoloSearchableDropdown } from '../../components/Wemolo'
import { useFormSaveStore } from '../../utils'
import { DeviceListType, DeviceType } from '../DeviceConfig'
import { transformDeviceToSelectValue, transformSelectValueToDevice } from '../DeviceConfig/utils/transformSelectValues'
import { StationDirections } from './station_types'
import { DeviceTypeEnum } from '../../data/enums'
import { StationContext } from './StationContext'
import { useSetDeviceForceUpdate } from '../DeviceConfig/utils'
import styles from './StationConfig.module.css'

export const StationConfig = ({
    devices,
    stationDeviceUUID,
    initialDirection,
    standalone = false,
    saveStation,
    deleteStation,
    onDeviceChange,
    onDirectionChange,
    stationType = DeviceTypeEnum.SCANNER
}: {
    devices: DeviceListType
    stationDeviceUUID: string | null | undefined
    initialDirection: string | null | undefined
    standalone?: boolean
    saveStation?: (device_direction: StationDirections, device_uuid?: string | null | undefined) => void
    deleteStation?: () => void
    onDeviceChange?: (device: DeviceType | null | undefined) => void
    onDirectionChange?: (direction: StationDirections) => void
    stationType: DeviceTypeEnum | null | undefined
}) => {
    const [deviceDirection, setDeviceDirection] = useState<StationDirections>()
    const [selectedDevice, setSelectedDevice] = useState<DeviceType | null | undefined>(undefined)
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const [isCleared, setIsCleared] = useState(false)
    const stationContext = useContext(StationContext)
    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate()

    const getSelectedDevice = () => {
        //in case user pressed x button on select, we need to return null to reset the value
        if (selectedDevice) {
            return transformDeviceToSelectValue(selectedDevice)
        } else if (isCleared) {
            return null
        } else if (stationDeviceUUID) {
            return transformDeviceToSelectValue({
                __typename: 'device',
                uuid: stationDeviceUUID,
                config_force_update: false
            })
        } else {
            return null
        }
    }
    useEffect(() => {
        if (stationDeviceUUID) {
            setSelectedDevice(null)
        }
    }, [stationDeviceUUID])

    const direction = initialDirection
    useEffect(() => {
        setDeviceDirection(direction as StationDirections)
    }, [direction])

    let device_uuid_to_send: string | null | undefined = undefined
    if (selectedDevice?.uuid === undefined) {
        device_uuid_to_send = stationDeviceUUID
    } else {
        device_uuid_to_send = selectedDevice?.uuid
    }

    const forceUpdateDisabled =
        !device_uuid_to_send ||
        !!stationContext.deviceData?.config_force_update ||
        stationContext.deviceData?.config_current_timestamp === stationContext.deviceData?.config_target_timestamp

    return (
        <div className={styles.root}>
            {!standalone && <FormSavePrompt />}

            <div className={classNames(standalone ? styles.columnStandalone : styles.column)}>
                <InputWrapper label="Linked Device">
                    <WemoloSearchableDropdown
                        placeholder="Choose"
                        clearable={standalone}
                        options={toMenuOptions(
                            devices
                                ?.filter((device) => device.type === stationType)
                                ?.map(transformDeviceToSelectValue) || []
                        )}
                        value={getSelectedDevice()?.id as string | undefined}
                        onChange={(id) => {
                            setFormChanges(true)
                            if (!id) {
                                setIsCleared(true)
                                setSelectedDevice(null)
                                onDeviceChange?.(transformSelectValueToDevice(null))
                            } else {
                                const option = { id, label: id }
                                setSelectedDevice(transformSelectValueToDevice(option))
                                onDeviceChange?.(transformSelectValueToDevice(option))
                            }
                        }}
                    />
                </InputWrapper>
                {stationType === DeviceTypeEnum.SCANNER && (
                    <InputWrapper label="Device direction">
                        <WemoloDropdown
                            size="medium"
                            value={deviceDirection}
                            options={[
                                { value: 'IN', label: 'IN' },
                                { value: 'OUT', label: 'OUT' }
                            ]}
                            onChange={(id) => {
                                if (id) {
                                    setFormChanges(true)
                                    setDeviceDirection(id as StationDirections)
                                    onDirectionChange?.(id as StationDirections)
                                }
                            }}
                        />
                    </InputWrapper>
                )}
            </div>

            {!standalone && (
                <Columns
                    extraStyles={{
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        flex: '30%'
                    }}
                >
                    <CustomButton
                        isLoading={stationContext.loadingSaveStation}
                        disabled={stationContext.loadingSaveStation}
                        onClick={() => {
                            if (stationContext.loadingSaveStation) return
                            if (window.confirm('Are you sure you want to unlink this device?')) {
                                saveStation?.(deviceDirection as StationDirections, null)
                            }
                        }}
                        extraProps={{ minWidth: '270px !important;' }}
                    >
                        Unlink Device From Station
                    </CustomButton>
                    <CustomButton
                        onClick={() => {
                            if (window.confirm('Are you sure you want to delete this station?')) {
                                deleteStation?.()
                            }
                        }}
                        isLoading={stationContext.isDeletingStation}
                        type={ButtonType.DELETE}
                        extraProps={{ minWidth: '270px !important;', marginTop: '15px' }}
                    >
                        Delete Station
                    </CustomButton>
                    <CustomButton
                        isLoading={stationContext.loadingSaveStation}
                        disabled={stationContext.loadingSaveStation}
                        onClick={() => {
                            if (stationContext.loadingSaveStation) return
                            const deviceDirectionToSend =
                                stationType === DeviceTypeEnum.SCANNER ? deviceDirection : null
                            setFormChanges(false)
                            saveStation?.(deviceDirectionToSend as StationDirections, device_uuid_to_send || null)
                        }}
                        type={ButtonType.SAVE}
                        extraProps={{ minWidth: '270px !important;', marginTop: '15px' }}
                    >
                        Save Station Config
                    </CustomButton>
                    <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                        <CustomButton
                            disabled={forceUpdateDisabled || loadingForceUpdate}
                            isLoading={loadingForceUpdate}
                            onClick={() => {
                                if (
                                    window.confirm(
                                        'Are you sure you want to force update the configuration for this device?'
                                    )
                                ) {
                                    if (device_uuid_to_send) {
                                        setDeviceForceUpdate({ device_uuid: device_uuid_to_send }).catch(() => {})
                                    } else {
                                        alert('No device data available')
                                    }
                                }
                            }}
                            type={ButtonType.DELETE}
                            extraProps={{ minWidth: '270px !important;', marginTop: '15px' }}
                        >
                            Force Update
                        </CustomButton>
                    </InfoTooltip>
                </Columns>
            )}
            <div />
        </div>
    )
}
